import { Box, Skeleton, Tooltip } from "@mui/material";
import HandshakeOutlinedIcon from "@mui/icons-material/HandshakeOutlined";
import { useEffect, useState } from "react";
import ApiRoutes from "../config/api.routes";
import AxiosFactory from "../services/http.service";
import { LandToken } from "../types/LandToken";
import { CURRENCY_NAME, EtTransactionType } from "../utils/constants";
import { auctionCurrentPrice, toReadable } from "../utils/methods";
import { resolveStandardStatus } from "../utils/standard-status";
import CurrencyLabel from "./CurrencyLabel";
import DigitFlip from "./DigitFlip";
import { Text } from "./Typography";

/**
 * The lands tables' TXN cell (txnLatestLandCol) — two lines, laid out like locationCol's:
 *
 *  1. The live price: the current bid while the land is at auction (the opening price until the
 *     first bid), or its highest pending offer while it is open for offers. "-" otherwise,
 *     including an offer listing nobody has offered on yet.
 *  2. The price it last sold for, or "-" if it never has.
 *
 * Everything is read off the row where it can be. The land lists carry no sale price, but they
 * do carry the latest auction, and when the current owner won it its winning bid is the last
 * sale — nearly every sold land. Only a land that changed hands some other way (an accepted
 * offer) or has been relisted since is looked up in its transaction history. Likewise GET
 * /eco-token serves each land's offers but /eco-bank/lands does not, so on the latter a land
 * open for offers fetches its own.
 */

type Tx = { type: string; data?: Record<string, any> | null; createdAt: string };

const SALE_TYPES: string[] = [EtTransactionType.AUCTION_END, EtTransactionType.ACCEPT_OFFER, "buy_token"];

const positive = (value: unknown) => !!value && Number(value) > 0;

// Amounts are passed on as strings: toReadable treats a long string as wei and a short one as
// whole units (offers are stored in whole units, auction prices in wei), but any number as wei.
const asAmount = (value: unknown) => (positive(value) ? String(value) : null);

// Where a sale keeps its price: an auction's winning bid, an accepted offer, a legacy direct buy.
const salePriceOf = (tx?: Tx) =>
  asAmount(tx?.data?.endingPrice) ?? asAmount(tx?.data?.price) ?? asAmount(tx?.data?.amount);

// `offers` keeps every status; the standing offer is the highest still pending.
const topOfferOf = (offers?: LandToken["offers"]) =>
  (offers ?? [])
    .filter((offer) => offer.status === "pending")
    .reduce<string | null>(
      (max, offer) => (max === null || Number(offer.amount) > Number(max) ? asAmount(offer.amount) : max),
      null
    );

// Shared by every cell, so a land shown by two tables — or re-rendered, or paged back to — is
// fetched once. An entry is reused while the land's latest transaction is unchanged (a new offer
// or sale moves `lastTxn`), and for a minute at most either way. An amount is null when there is
// none, and undefined when the request failed and nobody knows.
const MAX_AGE_MS = 60_000;
type Amount = string | null | undefined;
type Entry = { stamp: unknown; at: number; value: Promise<Amount> };
const cache = new Map<string, Entry>();

const cached = (key: string, stamp: unknown, load: () => Promise<Amount>) => {
  const hit = cache.get(key);
  if (hit && hit.stamp === stamp && Date.now() - hit.at < MAX_AGE_MS) {
    return hit.value;
  }
  const entry: Entry = { stamp, at: Date.now(), value: Promise.resolve(undefined) };
  entry.value = load().catch(() => {
    // Not cached, so the next mount retries — unless a newer request already took the slot.
    if (cache.get(key) === entry) {
      cache.delete(key);
    }
    return undefined;
  });
  cache.set(key, entry);
  return entry.value;
};

// No cancel token: a request outlives the cell that started it, since others share it.
const http = () => new AxiosFactory().axios;

const loadTopOffer = (tokenId: LandToken["id"]) =>
  http()
    .get(ApiRoutes.token.offers(String(tokenId)))
    .then((res) => topOfferOf(res.data));

const loadLastSale = async (nftId: number, everListedForOffers: boolean) => {
  const history = (eventType: string, limit: number) =>
    http()
      .get<Tx[]>(`${ApiRoutes.ecoBank.transactions.byNft(nftId)}&eventType=${eventType}&limit=${limit}`)
      .then((res) => res.data ?? []);

  // Both newest first. "sale" is end_auction + buy_token; an auction that ended without a bid
  // leaves a priceless end_auction behind, so look past the newest. An accepted offer files under
  // "itemOffer" instead, and only a land that has been open for offers can have one.
  const [sales, offerEvents] = await Promise.all([
    history("sale", 10),
    everListedForOffers ? history("itemOffer", 50) : Promise.resolve<Tx[]>([]),
  ]);
  const sale = sales.find((tx) => salePriceOf(tx) !== null);
  const accepted = offerEvents.find((tx) => tx.type === EtTransactionType.ACCEPT_OFFER && salePriceOf(tx) !== null);
  const newest =
    sale && accepted
      ? new Date(accepted.createdAt).getTime() > new Date(sale.createdAt).getTime()
        ? accepted
        : sale
      : sale ?? accepted;
  return salePriceOf(newest);
};

// The last sale price as far as the row can tell: null when the land has never sold, undefined
// when it has but the row can't say for how much.
const lastSaleFromRow = ({ etNft, transactions }: LandToken): string | null | undefined => {
  const owner = etNft?.ownerAddress?.toLowerCase();
  const winner = etNft?.etAuction?.winner?.toLowerCase();

  // Never sold: still with its minter and no auction won. A missing owner reads the same way, so
  // a list that doesn't serve one never fans out into a request per row.
  if (!etNft?.id || (!winner && (!owner || owner === etNft.minterAddress?.toLowerCase()))) {
    return null;
  }
  // The land's newest transaction is itself a sale.
  const latestTxn = transactions?.[0] as Tx | undefined;
  if (latestTxn && SALE_TYPES.includes(latestTxn.type) && salePriceOf(latestTxn)) {
    return salePriceOf(latestTxn);
  }
  // The current owner won the latest auction, so nothing has sold it on since.
  if (winner && winner === owner) {
    return asAmount(etNft.etAuction.endingPrice) ?? undefined;
  }
  return undefined;
};

// `key` null = nothing to fetch. A cell slot can be handed another land (TableComponent keys rows
// by index), so a result only counts for the key it was fetched for.
const useCachedAmount = (key: string | null, stamp: unknown, load: () => Promise<Amount>) => {
  const [result, setResult] = useState<{ key: string; value: Amount }>();

  useEffect(() => {
    if (!key) {
      return undefined;
    }
    let alive = true;
    cached(key, stamp, load).then((value) => alive && setResult({ key, value }));
    return () => {
      alive = false;
    };
    // `load` is rebuilt every render; key + stamp are what identify it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, stamp]);

  if (!key) {
    return { loading: false, value: null };
  }
  return result?.key === key ? { loading: false, value: result.value } : { loading: true, value: null };
};

const LandPriceCell = ({ land }: { land: LandToken }) => {
  const { id, etNft, offerStatus, offers, lastTxn } = land;
  const auction = etNft?.etAuction;

  // Live exactly when auctionStatusCol says Auction or Offer — it reads the same public status,
  // so an auction past its end is Pending there and "-" here.
  const live = resolveStandardStatus(land, { role: "public" })?.label;

  const auctionPrice = live === "Auction" ? asAmount(auctionCurrentPrice(auction)) : null;
  const fetchOffer = live === "Offer" && !offers;
  const offer = useCachedAmount(fetchOffer ? `offer:${id}` : null, lastTxn, () => loadTopOffer(id));
  const livePrice = live === "Offer" ? (fetchOffer ? offer.value : topOfferOf(offers)) : auctionPrice;

  const rowSale = lastSaleFromRow(land);
  const fetchSale = rowSale === undefined;
  const sale = useCachedAmount(fetchSale ? `sale:${etNft?.id}` : null, lastTxn, () =>
    loadLastSale(Number(etNft?.id), !!offerStatus)
  );
  const salePrice = fetchSale ? sale.value : rowSale;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
      <Text numeric contrast medium>
        {offer.loading ? (
          <Skeleton width={90} />
        ) : livePrice ? (
          <>
            {/* A new bid or offer flips the amount, as in the land page's price box. Scopes of
                their own: that box formats the figure differently, so sharing its store would
                flip on every hop between the two. */}
            <DigitFlip
              value={String(toReadable(livePrice, true, true, 2))}
              trackKey={etNft?.id}
              scope={live === "Offer" ? "txn-offer" : "txn-bid"}
            />
            <CurrencyLabel currency={CURRENCY_NAME} />
          </>
        ) : (
          "-"
        )}
      </Text>
      <Text numeric sx={{ color: "text.disabled", whiteSpace: "nowrap", alignItems: "center" }}>
        {sale.loading ? (
          <Skeleton width={80} />
        ) : salePrice ? (
          <>
            {/* Marks the figure as the last sale, named on hover by the same Tooltip as
                auctionStatusCol's and sized and spaced like its Auction/Offer icon. The Tooltip
                also labels the icon, which SvgIcon hides from screen readers unless told not to. */}
            <Tooltip arrow placement="bottom" title="Sold">
              <HandshakeOutlinedIcon aria-hidden={false} role="img" sx={{ fontSize: 14, mr: "4px" }} />
            </Tooltip>
            {`${toReadable(salePrice, true, true, 2)} ${CURRENCY_NAME}`}
          </>
        ) : salePrice === null ? (
          <Text small>Not Sold</Text>
        ) : (
          // The history lookup failed: don't claim either way.
          "-"
        )}
      </Text>
    </Box>
  );
};

export default LandPriceCell;
